import React, { useEffect, useState } from "react";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  AvatarGroup,
  IconButton,
  Stack,
  Divider,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  Cancel,
  Visibility,
  CalendarToday,
  ShoppingBag,
} from "@mui/icons-material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { useTheme } from "@mui/material/styles";
import { FaRupeeSign } from "react-icons/fa";

const Orders = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  let _id = secureLocalStorage.getItem("n_id");
  let token = secureLocalStorage.getItem("n_token");

  const [orderlist, setOrderList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Accepted");
  const [selectedType, setSelectedType] = useState("Buy");
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    orderId: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let data = {
        user_id: _id,
        order_status: selectedStatus,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API}website/my_order_status`,
        data,
        { headers: { token } },
      );
      setLoading(false);
      setOrderList(res.data.data || []);
    } catch (err) {
      console.log(err);
      setOrderList([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [selectedStatus]);

  const filteredOrders = orderlist.filter((order) => {
    const typeMatch = order.available_status === selectedType;
    return typeMatch;
  });

  const cancelOrder = async (orderid) => {
    try {
      let data = {
        order_id: orderid,
        user_id: _id,
        order_status: "Cancel",
        region: "yyyy",
      };
      await axios.post(
        `${process.env.REACT_APP_API}website/cancel_order`,
        data,
        {
          headers: { token },
        },
      );
      fetchOrders();
    } catch (error) {
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Cancelled":
        return "error";
      case "Accepted":
        return "warning";
      case "Shipped":
        return "info";
      case "Delivered":
        return "success";
      default:
        return "primary";
    }
  };

  const getStatusVariant = (status) =>
    status === "Cancel" ? "outlined" : "filled";

  const viewOrder = (items) => {
    secureLocalStorage.setItem("orderid", items.order_id);
    Navigate(`/track_order`);
  };

  const handleCancelConfirm = () => {
    if (cancelDialog.orderId) cancelOrder(cancelDialog.orderId);
    setCancelDialog({ open: false, orderId: null });
  };

  const handleCancelClose = () =>
    setCancelDialog({ open: false, orderId: null });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: { xs: 2, sm: 4 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            mb: { xs: 1, sm: 0 },
            color: "#160015",
          }}
        >
          My Orders
        </Typography>

        {/* Order Status Filter Buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {["Accepted", "Shipped", "Delivered", "Cancelled"].map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? "contained" : "outlined"}
              size={isMobile ? "small" : "medium"}
              onClick={() => setSelectedStatus(status)}
              sx={{
                borderRadius: 2,
                backgroundColor:
                  selectedStatus === status ? "#160015" : "transparent",
                color: selectedStatus === status ? "#fee894" : "#160015",
                borderColor: "#160015",
                "&:hover": {
                  backgroundColor:
                    selectedStatus === status
                      ? "#160015"
                      : "rgba(22, 0, 21, 0.1)",
                  borderColor: "#160015",
                },
              }}
            >
              {status}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Order Type Filter Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          width: "100%",
          mb: 3,
        }}
      >
        {["Buy", "Lease"].map((type) => (
          <Button
            key={type}
            variant={selectedType === type ? "contained" : "outlined"}
            size={isMobile ? "small" : "medium"}
            onClick={() => setSelectedType(type)}
            sx={{
              borderRadius: 2,
              flex: 1,
              backgroundColor:
                selectedType === type ? "#160015" : "transparent",
              color: selectedType === type ? "#fee894" : "#160015",
              borderColor: "#160015",
              "&:hover": {
                backgroundColor:
                  selectedType === type ? "#160015" : "rgba(22, 0, 21, 0.1)",
                borderColor: "#160015",
              },
            }}
          >
            {type === "Buy" ? "Buy Orders" : "Lease Orders"}
          </Button>
        ))}
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 6, sm: 10 },
            backgroundColor: "#fff",
            borderRadius: 3,
            border: "2px solid #f0f0f0",
          }}
        >
          <CircularProgress size={50} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading your orders...
          </Typography>
        </Box>
      ) : filteredOrders?.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: { xs: 4, sm: 8 },
            color: "#666",
            marginTop: { xs: 2, sm: 4 },
            backgroundColor: "#fff",
            borderRadius: 3,
            border: "2px solid #f0f0f0",
          }}
        >
          <ShoppingCartCheckoutIcon
            sx={{
              fontSize: { xs: 48, sm: 64 },
              mb: 2,
              opacity: 0.5,
              color: "#160015",
            }}
          />
          <Typography variant="h6" gutterBottom sx={{ color: "#160015" }}>
            No orders yet
          </Typography>
          <Typography variant="body2">
            Your orders will appear here once you make a purchase
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 3 }}>
            {filteredOrders.map((items) => (
              <Grid item xs={12} sm={6} md={4} key={items.order_id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    backgroundColor: "#fff",
                    border: "2px solid #160015",
                    borderRadius: 3,
                    "&:hover": {
                      transform: { sm: "translateY(-4px)" },
                      boxShadow: "0 8px 25px rgba(22, 0, 21, 0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 0,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ p: 2, pb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Chip
                          label={items.order_status}
                          color={getStatusColor(items.order_status)}
                          variant={getStatusVariant(items.order_status)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                          }}
                        />
                        
                      </Box>
                    </Box>

                    <Divider />

                    {/* Details */}
                    <Box sx={{ p: 2, flex: 1 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ShoppingCartCheckoutIcon
                            sx={{
                              fontSize: 18,
                              color: "#160015",
                              mr: 1,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            Order No:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 1, fontWeight: 500, color: "#160015" }}
                          >
                            {items.order_no}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarToday
                            sx={{
                              fontSize: 18,
                              color: "#160015",
                              mr: 1,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            Order date:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 1, fontWeight: 500, color: "#160015" }}
                          >
                            {items.order_date}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FaRupeeSign
                            style={{
                              fontSize: 16,
                              color: "#160015",
                              marginRight: 8,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            Total:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              ml: 1,
                              fontWeight: 600,
                              color: "#160015",
                            }}
                          >
                            ₹{items.grand_total}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Products + View */}
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <AvatarGroup
                        max={isMobile ? 3 : 4}
                        sx={{
                          "& .MuiAvatar-root": {
                            width: 32,
                            height: 32,
                            border: "2px solid #160015",
                          },
                        }}
                      >
                        {items?.products?.map((product, index) => (
                          <Avatar
                            key={index}
                            src={`${process.env.REACT_APP_API}uploads/${product.product_image}`}
                            alt="Product"
                            sx={{
                              border: "2px solid #fff",
                              backgroundColor: "#fef9e7",
                            }}
                          />
                        ))}
                      </AvatarGroup>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => viewOrder(items)}
                        sx={{
                          minWidth: "auto",
                          px: 2,
                          borderRadius: 2,
                          backgroundColor: "#160015",
                          color: "#fee894",
                          borderColor: "#160015",
                          "&:hover": {
                            backgroundColor: "rgba(22, 0, 21, 0.9)",
                            borderColor: "#160015",
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            border: "2px solid #160015",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            backgroundColor: "#160015",
            color: "#fee894",
          }}
        >
          <Typography variant="h6" component="span">
            Cancel Order
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCancelClose}
            sx={{
              color: "#160015",
              borderColor: "#160015",
              "&:hover": {
                backgroundColor: "rgba(22, 0, 21, 0.1)",
              },
            }}
          >
            Keep Order
          </Button>
          <Button
            onClick={handleCancelConfirm}
            variant="contained"
            autoFocus
            sx={{
              backgroundColor: "#160015",
              color: "#fee894",
              borderColor: "#160015",
              "&:hover": {
                backgroundColor: "rgba(22, 0, 21, 0.9)",
                borderColor: "#160015",
              },
            }}
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
